/**
 * Team directory client: the roster as every internal seat may see it.
 *
 * Mirrors the server wire shape (api/_shared/teamDirectory.ts
 * TeamDirectoryPerson). Visibility is decided SERVER-SIDE: the private
 * contact pair (home address, personal phone) arrives null for a seat that
 * may not see it, and `private_contact_visible` says whether the pair was
 * served at all. The page renders what it was given; nothing here hides a
 * value that arrived.
 *
 * The calendar helpers below (birthday and anniversary labels, the
 * completed-years count, the Upcoming window) are pure over 'YYYY-MM-DD'
 * strings. "Today" is the calendar date in the operating zone
 * (America/New_York), so a birthday reads the same on every laptop.
 */

import { useQuery } from '@tanstack/react-query';
import { roleNoun } from './people';
import { addDays, calendarDateIn, isoWeekMonday } from './capacity';
import { formatPhoneDisplay } from './phone';
import { apiErrorFrom } from './api';

export interface TeamDirectoryPerson {
  roster_id: string;
  full_name: string;
  preferred_name: string | null;
  credentials: string | null;
  unit_name: string | null;
  does_care_management: boolean;
  does_spice: boolean;
  /** staff_roster.billable_role (the "designation": Care Manager / SPICE
   *  Therapist / Admin / Manager / Owner) -- People's own Role column reads
   *  this (src/lib/people.ts billableRoleLabel); roleLine below falls back
   *  to it only when neither capability flag says anything. */
  billable_role: string | null;
  work_phone: string | null;
  work_email: string | null;
  has_photo: boolean;
  birthday_month: number | null;
  birthday_day: number | null;
  /** 'YYYY-MM-DD': the explicit anniversary, else the start date. */
  work_anniversary: string | null;
  /** 'YYYY-MM-DD': the start date as entered. */
  start_date: string | null;
  home_address: string | null;
  /** The home's five parts (0228); null on every part: text only. */
  home_address_line1?: string | null;
  home_address_line2?: string | null;
  home_address_city?: string | null;
  home_address_state?: string | null;
  home_address_zip?: string | null;
  /** The town and ZIP the capacity map places this person by
   *  (staff_roster.home_city / home_zip), private-tier with the address.
   *  Optional to tolerate an older cached wire shape. */
  home_city?: string | null;
  home_zip?: string | null;
  personal_phone: string | null;
  /** 0239: the lines printed under this person's name; null = none.
   *  Optional for older shapes. */
  signature_block?: string | null;
  /** This row is the viewer's own: they may edit their signature block. */
  is_you?: boolean;
}

export interface TeamDirectoryResponse {
  people: TeamDirectoryPerson[];
  /** True when the caller holds view_staff_private_contact and the private pair was served. */
  private_contact_visible: boolean;
  request_id?: string;
}

const base = (slug: string): string => `/api/t/${encodeURIComponent(slug)}/v1/team-directory`;

export const teamDirectoryApi = {
  async list(tenantSlug: string): Promise<TeamDirectoryResponse> {
    const r = await fetch(base(tenantSlug), { headers: { accept: 'application/json' } });
    if (!r.ok) throw await apiErrorFrom(r, `GET ${base(tenantSlug)}`);
    return (await r.json()) as TeamDirectoryResponse;
  },
};

/** The photo-serve URL for the directory (the view_staff_tools door). */
export function teamPhotoUrl(tenantSlug: string, rosterId: string): string {
  return `${base(tenantSlug)}/${encodeURIComponent(rosterId)}/photo`;
}

/**
 * The roster print: the directory as a Word document, at the same door.
 * `rosterIds`, when given a non-empty list, narrows the print to those
 * roster rows (the table's current filter) instead of the whole roster.
 */
export function rosterDocxUrl(tenantSlug: string, rosterIds?: readonly string[]): string {
  const url = `${base(tenantSlug)}/roster.docx`;
  if (!rosterIds || rosterIds.length === 0) return url;
  return `${url}?${new URLSearchParams({ ids: rosterIds.join(',') }).toString()}`;
}

/**
 * 'Staff Roster, MM-DD-YY.docx' for a 'YYYY-MM-DD' day: the fallback file
 * name, used only when the response carries none. It names no company:
 * the server names the file for the tenant, and the browser does not know
 * the tenant's company name. `filtered` appends a marker so a narrowed
 * print never looks like the whole roster on disk.
 */
export function rosterFilename(today: string, opts: { filtered?: boolean } = {}): string {
  const [y, m, d] = today.split('-');
  const suffix = opts.filtered ? ' (filtered)' : '';
  return `Staff Roster${suffix}, ${m}-${d}-${y!.slice(2)}.docx`;
}

export function teamDirectoryQueryKey(slug: string): readonly unknown[] {
  return ['team-directory', slug];
}

export function useTeamDirectory(slug: string, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: teamDirectoryQueryKey(slug),
    queryFn: () => teamDirectoryApi.list(slug),
    staleTime: 60_000,
    enabled: opts.enabled ?? true,
  });
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Today's calendar date in the operating zone. */
export function todayInOperatingZone(now: Date = new Date()): string {
  return calendarDateIn(now);
}

function parseYmd(ymd: string): [number, number, number] | null {
  const parts = ymd.split('-').map(Number);
  const [y, m, d] = parts;
  if (parts.length !== 3 || !y || !m || !d || m < 1 || m > 12 || d < 1 || d > 31) return null;
  return [y, m, d];
}

function isLeapYear(y: number): boolean {
  return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
}

function ymd(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** Whole days from `from` to `to`; negative when `to` is earlier. */
function daysBetween(from: string, to: string): number {
  const a = parseYmd(from);
  const b = parseYmd(to);
  if (!a || !b) return Number.NaN;
  return Math.round((Date.UTC(b[0], b[1] - 1, b[2]) - Date.UTC(a[0], a[1] - 1, a[2])) / 86_400_000);
}

/** '4 March' from a month/day pair; null when either is missing. */
export function birthdayLabel(month: number | null, day: number | null): string | null {
  if (month === null || day === null || month < 1 || month > 12) return null;
  return `${day} ${MONTHS_LONG[month - 1]}`;
}

/** '1 Oct 2023' from a 'YYYY-MM-DD'. */
function shortDateLabel(iso: string): string | null {
  const parts = parseYmd(iso);
  if (!parts) return null;
  const [y, m, d] = parts;
  return `${d} ${MONTHS_SHORT[m - 1]} ${y}`;
}

/** The date years of service count from: the start date, else the resolved anniversary. */
export function anniversaryStartDate(
  person: Pick<TeamDirectoryPerson, 'start_date' | 'work_anniversary'>,
): string | null {
  return person.start_date ?? person.work_anniversary ?? null;
}

/**
 * Whole years completed from `start` as of `today` (both 'YYYY-MM-DD').
 * Negative when the start is still ahead.
 */
export function completedYears(start: string, today: string): number {
  const s = parseYmd(start);
  const t = parseYmd(today);
  if (!s || !t) return Number.NaN;
  let years = t[0] - s[0];
  if (t[1] < s[1] || (t[1] === s[1] && t[2] < s[2])) years -= 1;
  return years;
}

function yearsLabel(n: number): string {
  return `${n} ${n === 1 ? 'year' : 'years'}`;
}

/**
 * 'Since 1 Oct 2023 · 2 years': the start date and the whole years
 * completed as of today. Under a year in there is no count; a start still
 * ahead reads 'Starts 1 Oct 2026'. Null when no date is on file.
 */
export function anniversaryLine(
  person: Pick<TeamDirectoryPerson, 'start_date' | 'work_anniversary'>,
  today: string = todayInOperatingZone(),
): string | null {
  const start = anniversaryStartDate(person);
  if (!start) return null;
  const label = shortDateLabel(start);
  if (!label) return null;
  const years = completedYears(start, today);
  if (Number.isNaN(years)) return null;
  if (years < 0) return `Starts ${label}`;
  return years >= 1 ? `Since ${label} · ${yearsLabel(years)}` : `Since ${label}`;
}

/**
 * True when `today` falls in the same calendar month as a completed work
 * anniversary -- the roster table's Since cell shows its party-popper mark
 * for the whole month, not only the exact day. False before a first year
 * is completed (a hire month is not yet an anniversary) and false with no
 * start date on file.
 */
export function isAnniversaryMonth(
  person: Pick<TeamDirectoryPerson, 'start_date' | 'work_anniversary'>,
  today: string = todayInOperatingZone(),
): boolean {
  const start = anniversaryStartDate(person);
  if (!start) return false;
  const startParts = parseYmd(start);
  const todayParts = parseYmd(today);
  if (!startParts || !todayParts) return false;
  if (startParts[1] !== todayParts[1]) return false;
  return completedYears(start, today) >= 1;
}

/** The billable_role designation's own word, cased to the roster's own
 *  sentence-case convention -- the fallback for a person neither capability
 *  flag describes (an owner, an admin, a manager). */
const DESIGNATION_ROLE_WORDS: Record<string, string> = {
  care_manager: 'Care manager',
  spice_therapist: 'SPICE therapist',
  admin: 'Admin',
  manager: 'Manager',
  owner: 'Owner',
};

/**
 * The role noun with its first letter up, so it can open a line. The two
 * capability flags decide it when either is on (care manager / SPICE
 * therapist / both); otherwise the person's billable_role designation
 * supplies the word (Owner, Admin, Manager) -- the same designation
 * People's own Role column reads (./people.ts billableRoleLabel), cased to
 * this page's own convention rather than borrowed verbatim. "Team member"
 * is the last resort, for a person neither says anything about.
 */
export function roleLine(
  person: Pick<TeamDirectoryPerson, 'does_care_management' | 'does_spice' | 'billable_role'>,
): string {
  if (!person.does_care_management && !person.does_spice) {
    const word = person.billable_role ? DESIGNATION_ROLE_WORDS[person.billable_role] : undefined;
    if (word) return word;
  }
  const noun = roleNoun(person);
  return noun.charAt(0).toUpperCase() + noun.slice(1);
}

/**
 * The name an occasion is announced under: the preferred name (else the
 * first name) plus the last initial, "Jill K.", so two people who share a
 * first name never read as one. A single-word name stands alone.
 */
export function occasionName(person: Pick<TeamDirectoryPerson, 'full_name' | 'preferred_name'>): string {
  const words = person.full_name.trim().split(/\s+/).filter(Boolean);
  const first = person.preferred_name?.trim() || words[0] || person.full_name;
  const last = words.length > 1 ? words[words.length - 1] : '';
  const initial = last.replace(/[^A-Za-z]/g, '').charAt(0).toUpperCase();
  return initial ? `${first} ${initial}.` : first;
}

/**
 * The next calendar date on or after `today` with the given month and day.
 * A 29 February lands on 28 February in a year without one.
 */
function nextOccurrence(month: number, day: number, today: string): string | null {
  const t = parseYmd(today);
  if (!t || month < 1 || month > 12 || day < 1 || day > 31) return null;
  for (const year of [t[0], t[0] + 1]) {
    const d = month === 2 && day === 29 && !isLeapYear(year) ? 28 : day;
    const candidate = ymd(year, month, d);
    if (daysBetween(today, candidate) >= 0) return candidate;
  }
  return null;
}

/**
 * The Coming up window: six weeks (42 days) out from today, inclusive of
 * both ends. Wide enough that the This week / Next week / Later this
 * month / Next month grouping below has somewhere to put rows without
 * crowding the near term.
 */
export const UPCOMING_WINDOW_DAYS = 42;

export type OccasionKind = 'birthday' | 'anniversary';

/** The Coming up band and popover's leading marks. Real emoji characters,
 *  rendered as text -- never an icon font or an image -- so they read the
 *  same as any other string. */
export const BIRTHDAY_MARK = '\u{1F382}';
export const ANNIVERSARY_MARK = '\u{1F389}';

/** The Coming up band shows at most this many chips before "See all N"
 *  opens the full grouped list in a popover. Deliberately narrower than
 *  UPCOMING_VISIBLE_LIMIT (the popover's own, now-uncapped list): a flat
 *  band of chips reads worse past one short wrapped line, where the
 *  popover has the room to show everything grouped. */
export const COMING_UP_BAND_LIMIT = 6;

export interface UpcomingOccasion {
  roster_id: string;
  kind: OccasionKind;
  /** The day it falls on, 'YYYY-MM-DD'. */
  date: string;
  /** Years reached on the day; null for a birthday. */
  years: number | null;
  /** The name the occasion is announced under (preferred, else first). */
  name: string;
}

/** 'birthday', or the years reached ('5 years', '1 year'). */
export function occasionEventWords(o: Pick<UpcomingOccasion, 'kind' | 'years'>): string {
  return o.kind === 'birthday' ? 'birthday' : yearsLabel(o.years ?? 0);
}

/** '14 September' from a occasion's 'YYYY-MM-DD' date. */
export function occasionDateLabel(date: string): string {
  const parts = parseYmd(date)!;
  return `${parts[2]} ${MONTHS_LONG[parts[1] - 1]}`;
}

/** '17 Sep': day and abbreviated month, no year -- the Coming up band
 *  chip's compact date (occasionDateLabel's full month name is the
 *  popover's, where there is room for it). */
export function occasionDateShortLabel(date: string): string {
  const parts = parseYmd(date)!;
  return `${parts[2]} ${MONTHS_SHORT[parts[1] - 1]}`;
}

/**
 * The Coming up band chip's visible text: the mark, the name, and (for an
 * anniversary only) the years reached, then the short date -- '🎂 Lynn ·
 * 17 Sep' or '🎉 Jill · 5 years · 1 Oct'. A birthday chip carries no
 * "birthday" word (the mark IS the word); occasionChipAriaLabel supplies
 * it for anyone who cannot see the mark.
 */
export function occasionChipText(o: Pick<UpcomingOccasion, 'kind' | 'name' | 'date' | 'years'>): string {
  const mark = o.kind === 'birthday' ? BIRTHDAY_MARK : ANNIVERSARY_MARK;
  const parts = [o.name];
  if (o.kind === 'anniversary') parts.push(occasionEventWords(o));
  parts.push(occasionDateShortLabel(o.date));
  return `${mark} ${parts.join(' · ')}`;
}

/**
 * The accessible name for a Coming up mark, spelling out in words what the
 * emoji alone conveys visually: 'Birthday: Lynn, 17 September' or 'Work
 * anniversary: Jill, 5 years, 1 October'. Shared by the band chip and the
 * popover's row, so the two surfaces say the same thing.
 */
export function occasionChipAriaLabel(o: Pick<UpcomingOccasion, 'kind' | 'name' | 'date' | 'years'>): string {
  const word = o.kind === 'birthday' ? 'Birthday' : 'Work anniversary';
  const date = occasionDateLabel(o.date);
  const parts = o.kind === 'birthday' ? [o.name, date] : [o.name, occasionEventWords(o), date];
  return `${word}: ${parts.join(', ')}`;
}

/**
 * The birthdays and work anniversaries falling from `today` through
 * UPCOMING_WINDOW_DAYS days out, inclusive, in date order (ties break on
 * name). An anniversary counts from anniversaryStartDate and is listed
 * only once a first year has been completed on the day.
 */
export function upcomingOccasions(
  people: ReadonlyArray<TeamDirectoryPerson>,
  today: string = todayInOperatingZone(),
): UpcomingOccasion[] {
  const out: UpcomingOccasion[] = [];
  const last = addDays(today, UPCOMING_WINDOW_DAYS);
  const inWindow = (date: string): boolean => daysBetween(today, date) >= 0 && daysBetween(date, last) >= 0;

  for (const person of people) {
    const name = occasionName(person);
    if (person.birthday_month !== null && person.birthday_day !== null) {
      const date = nextOccurrence(person.birthday_month, person.birthday_day, today);
      if (date && inWindow(date)) {
        out.push({ roster_id: person.roster_id, kind: 'birthday', date, years: null, name });
      }
    }
    const start = anniversaryStartDate(person);
    const startParts = start ? parseYmd(start) : null;
    if (startParts) {
      const date = nextOccurrence(startParts[1], startParts[2], today);
      if (date && inWindow(date)) {
        const years = parseYmd(date)![0] - startParts[0];
        if (years >= 1) {
          out.push({ roster_id: person.roster_id, kind: 'anniversary', date, years, name });
        }
      }
    }
  }

  return out.sort((a, b) => (a.date === b.date ? a.name.localeCompare(b.name) : a.date < b.date ? -1 : 1));
}

export type UpcomingBucket = 'this_week' | 'next_week' | 'later_this_month' | 'next_month';

const BUCKET_ORDER: readonly UpcomingBucket[] = ['this_week', 'next_week', 'later_this_month', 'next_month'];

export const UPCOMING_BUCKET_LABELS: Record<UpcomingBucket, string> = {
  this_week: 'This week',
  next_week: 'Next week',
  later_this_month: 'Later this month',
  next_month: 'Next month',
};

function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/**
 * Which Coming up bucket a date falls into, relative to `today`. Weeks are
 * Monday..Sunday (isoWeekMonday, the house convention the capacity board
 * already uses): "This week" runs through the coming Sunday, "Next week"
 * the Monday after through its Sunday. Once both weeks are behind it, the
 * bucket reads by calendar month: whatever is left of the current month is
 * "Later this month" (empty when the two weeks already ran past month
 * end), everything after that is "Next month". A date the two weeks
 * already cover keeps its week label even where that week crosses into
 * the next calendar month -- "next week" is next week regardless of which
 * month it lands in.
 */
export function bucketFor(date: string, today: string): UpcomingBucket {
  const monday = isoWeekMonday(today);
  const thisWeekEnd = addDays(monday, 6);
  const nextWeekEnd = addDays(monday, 13);
  if (date <= thisWeekEnd) return 'this_week';
  if (date <= nextWeekEnd) return 'next_week';
  const [ty, tm] = parseYmd(today)!;
  const monthEnd = ymd(ty, tm, daysInMonth(ty, tm));
  return date <= monthEnd ? 'later_this_month' : 'next_month';
}

export interface UpcomingGroup {
  bucket: UpcomingBucket;
  label: string;
  occasions: UpcomingOccasion[];
}

/**
 * Occasions (already in date order) partitioned into the four Coming up
 * buckets, in bucket order; a bucket with nothing in it is left out
 * entirely rather than rendering an empty header.
 */
export function groupUpcomingOccasions(
  occasions: readonly UpcomingOccasion[],
  today: string = todayInOperatingZone(),
): UpcomingGroup[] {
  const byBucket = new Map<UpcomingBucket, UpcomingOccasion[]>();
  for (const o of occasions) {
    const bucket = bucketFor(o.date, today);
    const arr = byBucket.get(bucket) ?? [];
    arr.push(o);
    byBucket.set(bucket, arr);
  }
  return BUCKET_ORDER.filter((b) => byBucket.has(b)).map((bucket) => ({
    bucket,
    label: UPCOMING_BUCKET_LABELS[bucket],
    occasions: byBucket.get(bucket)!,
  }));
}

/**
 * A general cap for a grouped occasions view, applied across bucket order.
 * Not currently used by the Coming up band (COMING_UP_BAND_LIMIT, a flat
 * cap of 6) or its popover (ComingUpPanel renders every group uncapped) --
 * kept as the general-purpose pair with limitUpcomingGroups below for a
 * future grouped view that wants a cap.
 */
export const UPCOMING_VISIBLE_LIMIT = 8;

export interface LimitedUpcomingGroups {
  groups: UpcomingGroup[];
  /** How many occasions the limited view shows. */
  shown: number;
  /** How many occasions exist across every group. */
  total: number;
}

/**
 * The first `limit` occasions across the groups (bucket order preserved),
 * dropping a bucket entirely once it has nothing left to show rather than
 * rendering an empty header. `total` is the full count, for the "See all"
 * control's own count and for deciding whether it renders at all.
 */
export function limitUpcomingGroups(
  groups: readonly UpcomingGroup[],
  limit: number = UPCOMING_VISIBLE_LIMIT,
): LimitedUpcomingGroups {
  const total = groups.reduce((n, g) => n + g.occasions.length, 0);
  let remaining = limit;
  const out: UpcomingGroup[] = [];
  for (const g of groups) {
    if (remaining <= 0) break;
    const occasions = g.occasions.slice(0, remaining);
    if (occasions.length > 0) out.push({ ...g, occasions });
    remaining -= occasions.length;
  }
  return { groups: out, shown: Math.min(limit, total), total };
}

/**
 * The "Copy contact" block: name and credentials, role and unit, then the
 * WORK phone and email that are on file. One fact per line, no line for a
 * fact that is not on file, and never the private pair or the calendar
 * facts: this is what someone pastes into an email or a phone.
 */
export function contactCardText(person: TeamDirectoryPerson): string {
  const lines: string[] = [];
  lines.push(person.credentials ? `${person.full_name}, ${person.credentials}` : person.full_name);
  lines.push(person.unit_name ? `${roleLine(person)}, ${person.unit_name}` : roleLine(person));
  if (person.work_phone) lines.push(`Phone: ${formatPhoneDisplay(person.work_phone)}`);
  if (person.work_email) lines.push(`Email: ${person.work_email}`);
  return lines.join('\n');
}

// =====================================================================
// Roster table: sort + filter.
// =====================================================================

export type RosterSortField = 'unit' | 'name' | 'role' | 'since';
export type RosterSortDir = 'asc' | 'desc';

export interface RosterSort {
  field: RosterSortField;
  dir: RosterSortDir;
}

/** Unit, then name -- the roster's own former card-grouping order. */
export const DEFAULT_ROSTER_SORT: RosterSort = { field: 'unit', dir: 'asc' };

/** The date "since" sorts by: start_date, else work_anniversary, same as
 *  the anniversary line itself. Null when neither is on file. */
export function rosterSinceValue(person: TeamDirectoryPerson): string | null {
  return anniversaryStartDate(person);
}

export function nextRosterSort(current: RosterSort, field: RosterSortField): RosterSort {
  if (current.field !== field) return { field, dir: 'asc' };
  return { field, dir: current.dir === 'asc' ? 'desc' : 'asc' };
}

/**
 * Sorts the roster by the given column. A person with no unit (for `unit`)
 * or no start/anniversary date on file (for `since`) sorts LAST in either
 * direction -- an unknown value never floats to the top just because the
 * direction flipped. Every field ties on full_name (itself excepted).
 */
export function sortRoster(
  people: readonly TeamDirectoryPerson[],
  sort: RosterSort = DEFAULT_ROSTER_SORT,
): TeamDirectoryPerson[] {
  const dirMul = sort.dir === 'asc' ? 1 : -1;
  const byName = (a: TeamDirectoryPerson, b: TeamDirectoryPerson): number => a.full_name.localeCompare(b.full_name);

  return [...people].sort((a, b) => {
    switch (sort.field) {
      case 'name':
        return dirMul * byName(a, b);
      case 'role': {
        const cmp = roleLine(a).localeCompare(roleLine(b));
        return cmp !== 0 ? dirMul * cmp : byName(a, b);
      }
      case 'unit': {
        const aNone = a.unit_name === null;
        const bNone = b.unit_name === null;
        if (aNone !== bNone) return aNone ? 1 : -1;
        if (aNone) {
          // Within the no-unit run: Office first, then No unit yet.
          const aOffice = isOfficePerson(a);
          const bOffice = isOfficePerson(b);
          if (aOffice !== bOffice) return aOffice ? -1 : 1;
          return byName(a, b);
        }
        const cmp = a.unit_name!.localeCompare(b.unit_name!);
        return cmp !== 0 ? dirMul * cmp : byName(a, b);
      }
      case 'since': {
        const av = rosterSinceValue(a);
        const bv = rosterSinceValue(b);
        const aNone = av === null;
        const bNone = bv === null;
        if (aNone !== bNone) return aNone ? 1 : -1;
        if (aNone) {
          // Within the no-unit run: Office first, then No unit yet.
          const aOffice = isOfficePerson(a);
          const bOffice = isOfficePerson(b);
          if (aOffice !== bOffice) return aOffice ? -1 : 1;
          return byName(a, b);
        }
        const cmp = av! < bv! ? -1 : av! > bv! ? 1 : 0;
        return cmp !== 0 ? dirMul * cmp : byName(a, b);
      }
    }
  });
}

export type RosterGroupKind = 'unit' | 'office' | 'no_unit';

export interface RosterRowGroup {
  kind: RosterGroupKind;
  /** The header text: the unit's name, "Office", or "No unit yet". */
  label: string;
  /** The unit's name for a unit group; null for the office and no-unit groups. */
  unitName: string | null;
  people: TeamDirectoryPerson[];
}

/** Office staff and leadership: no unit, and not doing care management or SPICE. */
export function isOfficePerson(p: Pick<TeamDirectoryPerson, 'unit_name' | 'does_care_management' | 'does_spice'>): boolean {
  return p.unit_name === null && !p.does_care_management && !p.does_spice;
}

export const OFFICE_GROUP_LABEL = 'Office';
export const NO_UNIT_GROUP_LABEL = 'No unit yet';

function groupKeyFor(p: TeamDirectoryPerson): { kind: RosterGroupKind; label: string; unitName: string | null } {
  if (p.unit_name !== null) return { kind: 'unit', label: p.unit_name, unitName: p.unit_name };
  if (isOfficePerson(p)) return { kind: 'office', label: OFFICE_GROUP_LABEL, unitName: null };
  return { kind: 'no_unit', label: NO_UNIT_GROUP_LABEL, unitName: null };
}

/**
 * Partitions an ALREADY unit-sorted roster (sortRoster with field 'unit')
 * into its contiguous runs, for the table's group header rows: one per
 * unit, then "Office" (no unit, not doing care management or SPICE), then
 * "No unit yet" (a care manager or therapist whose unit is not set on the
 * People page). Shown only when the table is sorted by unit, so this never
 * runs against a list sorted any other way. Two rows with the same unit_name
 * but NOT adjacent (the input was not unit-sorted) become two separate
 * groups rather than merging; that is a caller error, not a case this
 * partitions around.
 */
export function groupSortedRosterByUnit(people: readonly TeamDirectoryPerson[]): RosterRowGroup[] {
  const groups: RosterRowGroup[] = [];
  for (const p of people) {
    const key = groupKeyFor(p);
    const last = groups[groups.length - 1];
    if (last && last.kind === key.kind && last.unitName === key.unitName) {
      last.people.push(p);
    } else {
      groups.push({ ...key, people: [p] });
    }
  }
  return groups;
}

export interface RosterFilters {
  search: string;
  /** null = every unit (including the no-unit bucket). */
  unit: string | null;
  /** null = every role. */
  role: string | null;
}

export const EMPTY_ROSTER_FILTERS: RosterFilters = { search: '', unit: null, role: null };

/** Every distinct unit_name on the roster, sorted -- the Unit select's options. */
export function rosterUnitOptions(people: readonly TeamDirectoryPerson[]): string[] {
  const names = new Set(people.map((p) => p.unit_name).filter((n): n is string => n !== null));
  return [...names].sort((a, b) => a.localeCompare(b));
}

/** Every distinct role line on the roster, sorted -- the Role select's options. */
export function rosterRoleOptions(people: readonly TeamDirectoryPerson[]): string[] {
  const roles = new Set(people.map((p) => roleLine(p)));
  return [...roles].sort((a, b) => a.localeCompare(b));
}

/** The search text against name, unit, role and work email -- the same
 *  fields the roster search has always matched against. */
function matchesSearch(person: TeamDirectoryPerson, q: string): boolean {
  if (q.length === 0) return true;
  return [person.full_name, person.preferred_name, person.credentials, person.unit_name, person.work_email, roleLine(person)]
    .filter((v): v is string => typeof v === 'string' && v.length > 0)
    .join(' ')
    .toLowerCase()
    .includes(q);
}

/** The roster narrowed by search text, unit and role, all combined (AND). */
export function filterRoster(
  people: readonly TeamDirectoryPerson[],
  filters: RosterFilters,
): TeamDirectoryPerson[] {
  const q = filters.search.trim().toLowerCase();
  return people.filter(
    (p) =>
      matchesSearch(p, q) &&
      (filters.unit === null || p.unit_name === filters.unit) &&
      (filters.role === null || roleLine(p) === filters.role),
  );
}
